import { Box, Tab, Tabs } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useDeleteCertificationMutation,
  useGetCertificationQuery,
  useGetMyCertificationsQuery,
  useShareCertificationMutation,
} from "../../store/certification/CertificationSlice";
import CustomTable from "../../theme/style-components/CustomTable";
import TabTitle from "../TabTitle";
import CustomButton from "../../theme/style-components/CustomButton";
import { useGetEmployeeMyEmpObjQuery } from "../../store/employee/EmployeeApiSlice";
import { useSelector } from "react-redux";
import { selectCurrentUserObject } from "../../store/auth/authSlice";

const Certificate = () => {
  const userType = localStorage.getItem("userType");
  if (userType === "ADMIN") {
    return <AdminView />;
  } else {
    return <EmployeeTabsView />;
  }
};
export default Certificate;

const AdminView = () => {
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const user = useSelector(selectCurrentUserObject);
  const [query, setQuery] = useState(`org_id=${user?.company}`);
  const [CertificateToDisplay, setCertificateToDisplay] = useState([]);
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [isMenuClicked, setIsMenuClicked] = useState(false);
  const [selectedCertificated, setSelectedCertificate] = useState([]);
  const [deleteCertification] = useDeleteCertificationMutation();
  const [isEditCertification, setIsEditCertification] = useState(false);
  const userType = localStorage.getItem("userType");

  useEffect(() => {
    let updatedQuery = `org_id=${user?.company}`;
    if (searchText) {
      updatedQuery += `&search=${encodeURIComponent(searchText)}`;
    }
    setQuery(updatedQuery);
  }, [searchText]);

  const {
    isLoading,
    data: CertificationsReturned,
    error,
    refetch: refetchCertification,
  } = useGetCertificationQuery({ page, query },{skip: !user?.company});

  const [shareCertification, { isLoading: isSharing, error: shareError }] =
    useShareCertificationMutation();

  const {
    data: employeeData,
    error: isErrorEmployeeData,
    refetch: refetchEmployee,
  } = useGetEmployeeMyEmpObjQuery();

  useEffect(() => {
    if (!isLoading && CertificationsReturned) {
      setCertificateToDisplay(CertificationsReturned?.data || []);
      setCount(CertificationsReturned?.page_details?.count || 0);
    }
  }, [isLoading, CertificationsReturned]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error occurred: {error.message}</div>;

  const handleCreateClick = () => navigate("/certifications/add");

  const onPageChange = (newPage) => setPage(newPage);

  const onRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const setSelectedData = (item) => setSelectedCertificate(item);

  const handleEditClick = () => {
    if (selectedCertificated?.id) {
      setIsEditCertification(true);
      navigate(`/certification/edit/${selectedCertificated.id}`);
    }
  };

  const handleAssignMeCertification = async (certificateId) => {
    if (employeeData?.data?.id && certificateId) {
      try {
        const response = await shareCertification({
          employee_ids: [Number(employeeData?.data?.id)],
          certificate_id: certificateId,
        })
          .unwrap()
          .then((res) => {
            toast.success("Certificate assigned successfully");
          });
      } catch (err) {
        console.error("Error sharing certificate:", err);
      }
    }
  };

  const handleDeleteClick = async () => {
    if (selectedCertificated?.id) {
      try {
        await deleteCertification({
          certification_id: selectedCertificated.id,
        }).unwrap();
        toast.success("Certificate deleted successfully");

        // Remove deleted certificate from local state
        setCertificateToDisplay((prevData) =>
          prevData.filter((cert) => cert.id !== selectedCertificated.id)
        );

        // Refetch data
        refetchCertification();
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("Something went wrong while deleting the certificate");
      }
    } else {
      toast.error("No certificate selected to delete");
    }
  };

  const handleOnSearch = (event) => setSearchText(event.target.value);

  const CertificationData =
    CertificateToDisplay?.length > 0
      ? CertificateToDisplay.map((data) => ({
        id: data.id,
        name: data.name || "N/A",
        description: data.description || "N/A",
        link: data.link ? (
          <a
            href={data.link}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "blue", textDecoration: "underline" }}
          >
            {data.link}
          </a>
        ) : (
          "N/A"
        ),
        assignCertificate: (
          userType !== "ADMIN" &&
          <CustomButton
            color="primary"
            type="button"
            buttonName="Assign Me"
            onClick={() => { handleAssignMeCertification(data?.id) }}
          />
        ),
        created_at: data.created_at || "N/A",
        updated_at: data.updated_at || "N/A",
        action: userType === "ADMIN" ? "ACTION" : null,
      }))
      : [];

  const columns = [
    { id: "name", label: "Name" },
    { id: "description", label: "Description" },
    { id: "link", label: "Link" },
    ...(userType !== "ADMIN" ? [{ id: "assignCertificate", label: "Assign Certificate" }] : []),
    ...(userType === "ADMIN" ? [{ id: "action", label: "Action" }] : []),
  ];

  return (
    <>
      <Box>
        <TabTitle title="Certifications" />
        <CustomTable
          title="Certificate List"
          page={page}
          count={count}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          handleCreateClick={handleCreateClick}
          createButtonName="Add Certificate"
          isShowCreateButton={userType === "ADMIN" ? true : false}
          isShowEditMenu
          handleEditClick={handleEditClick}
          isShowDeleteMenu
          handleDelteClick={handleDeleteClick}
          isActionVisible={userType === "ADMIN" ? true : false}
          setSelectedData={setSelectedData}
          data={CertificationData}
          columns={columns}
          isShowSearch
          handleOnSearch={handleOnSearch}
          searchText={searchText}
          searchPlaceholder="Search Name"
          isMenuClicked={isMenuClicked}
          setIsMenuClicked={setIsMenuClicked}
        />
      </Box>
    </>
  );
};

const EmployeeView = () => {
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const user = useSelector(selectCurrentUserObject);
  const [query, setQuery] = useState(`org_id=${user?.company}`);
  const [CertificateToDisplay, setCertificateToDisplay] = useState([]);
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [isMenuClicked, setIsMenuClicked] = useState(false);
  const [selectedCertificated, setSelectedCertificate] = useState([]);
  const [deleteCertification] = useDeleteCertificationMutation();
  const [isEditCertification, setIsEditCertification] = useState(false);

  useEffect(() => {
    let updatedQuery = `org_id=${user?.company}`;
    if (searchText) {
      updatedQuery += `&search=${encodeURIComponent(searchText)}`;
    }
    setQuery(updatedQuery);
  }, [searchText]);

  const {
    isLoading,
    data: CertificationsReturned,
    error,
    refetch: refetchCertification,
  } = useGetMyCertificationsQuery({ page, query });

  useEffect(() => {
    if (!isLoading && CertificationsReturned) {
      setCertificateToDisplay(CertificationsReturned?.data || []);
      setCount(CertificationsReturned?.page_details?.count || 0);
    }
  }, [isLoading, CertificationsReturned]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error occurred: {error.message}</div>;

  const onPageChange = (newPage) => setPage(newPage);

  const onRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const setSelectedData = (item) => setSelectedCertificate(item);

  const handleEditClick = () => {
    if (selectedCertificated?.id) {
      setIsEditCertification(true);
      navigate(`/certification/edit/my/${selectedCertificated.id}`);
    }
  };

  const handleOnSearch = (event) => setSearchText(event.target.value);

  const CertificationData =
    CertificateToDisplay?.length > 0
      ? CertificateToDisplay.map((data) => ({
        id: data.id,
        name: data.certificate_data[0].name || "N/A",
        description: data.certificate_data[0].description || "N/A",
        mark_completed: data.mark_completed ? "Yes" : "No" || "N/A",
        progress_percentage: data.progress_percentage || "N/A",
        proof_of_certification: data.proof_of_certification || "N/A",
        link: data.certificate_data[0].link ? (
          <a
            href={data.certificate_data[0].link}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "blue", textDecoration: "underline" }}
          >
            {data.certificate_data[0].link}
          </a>
        ) : (
          "N/A"
        ),
        created_at: data.created_at || "N/A",
        updated_at: data.updated_at || "N/A",
        action: "ACTION",
      }))
      : [];

  const columns = [
    { id: "name", label: "Name" },
    { id: "description", label: "Description" },
    { id: "link", label: "Link" },
    { id: "mark_completed", label: "Mark Completed" },
    { id: "progress_percentage", label: "Progress Percentage" },
    { id: "proof_of_certification", label: "Proof of Certification" },
    { id: "action", label: "Action" },
  ];

  return (
    <>
      <Box>
        <TabTitle title="My Certifications" />
        <CustomTable
          title="Certificate List"
          page={page}
          count={count}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          isShowCreateButton={false}
          isShowEditMenu
          handleEditClick={handleEditClick}
          isActionVisible
          setSelectedData={setSelectedData}
          data={CertificationData}
          columns={columns}
          isShowSearch
          handleOnSearch={handleOnSearch}
          searchText={searchText}
          searchPlaceholder="Search here..."
          isMenuClicked={isMenuClicked}
          setIsMenuClicked={setIsMenuClicked}
        />
      </Box>
    </>
  );
};

const EmployeeTabsView = () => {
  const [tabIndex, setTabIndex] = useState(0);

  return (
    <Box>
      <TabTitle title="Certifications" />
      <Tabs value={tabIndex} onChange={(_, newIndex) => setTabIndex(newIndex)} sx={{ marginBottom: "30px", backgroundColor: "#f8f9fa" }}>
        <Tab label="All Certificates" />
        <Tab label="My Certificates" />
      </Tabs>
      {tabIndex === 0 ? <AdminView /> : <EmployeeView />}
    </Box>
  );
};
